import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import {
  BuildingPlan,
  PlanAnnotation,
  PlanDragItem,
  PlanPlaceableItem,
  PlanPoint,
} from "./planTypes";
import { getAttributeColor, statusColor, LettingStatus } from "./planColors";
import { PlanSidebar } from "./planSidebar";
import { PlanCanvas } from "./planCanvas";
import { PlanTenantList } from "./planTenantList";
import {
  buildUnitTenantMap,
  buildAttributeTenantMap,
  groupAnnotationsByTenant,
  invertUnitTenants,
  PlanListItem,
} from "./planTenants";

interface PlattegrondTabProps {
  building: BuildingDTO;
}

interface ContourTarget {
  targetType: "unit" | "attribute";
  id: string;
  name: string;
  color: string;
}

const annKey = (a: PlanAnnotation) =>
  `${a.targetType}:${a.targetType === "unit" ? a.unitId : a.buildingAttribute?.id}`;

export const PlattegrondTab: React.FC<PlattegrondTabProps> = ({ building }) => {
  const { session } = useSession();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState<BuildingPlan[]>([]);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [unitStatusById, setUnitStatusById] = useState<Record<string, LettingStatus>>({});
  const [unitTenantsById, setUnitTenantsById] = useState<Record<string, string[]>>({});
  const [mode, setMode] = useState<"grid" | "editor">("grid");
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [imageByPlan, setImageByPlan] = useState<Record<number, { dataUrl: string; mimeType: string }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [panelHeight, setPanelHeight] = useState(560);
  const [contourTarget, setContourTarget] = useState<ContourTarget | null>(null);
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const [nameModal, setNameModal] = useState<{ mode: "create" | "rename"; planId?: number; value: string } | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);
  const [gridQuery, setGridQuery] = useState(""); // free-text filter on the grid (unit / attribute / plan name)
  const [focusAttributeId, setFocusAttributeId] = useState<number | null>(null); // deep-link: only plans with this attribute
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const selectedPlan = useMemo(() => plans.find((p) => p.id === selectedPlanId) || null, [plans, selectedPlanId]);
  const units: UnitDTO[] = (building.units as UnitDTO[]) || [];
  const attributesById = useMemo(() => {
    const map: Record<number, any> = {};
    attributes.forEach((a) => (map[a.id] = a));
    return map;
  }, [attributes]);

  // ── Data loading ─────────────────────────────────────────────────────────────────────
  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/building-plans/building/${building.id}`, { method: "GET" }, session);
      const data: BuildingPlan[] = res.ok ? await res.json() : [];
      setPlans(data);
      return data;
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Fout bij ophalen van plattegronden");
      return [];
    }
  }, [building.id, session]);

  const fetchAttributes = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/building-attributes/building/${building.id}`, { method: "GET" }, session);
      setAttributes(res.ok ? await res.json() : []);
    } catch {
      setAttributes([]);
    }
  }, [building.id, session]);

  // Derive each unit's letting status from the building's non-closed contracts (for pin colours),
  // plus a unitId → tenant names map used by the "huurders op deze plattegrond" list.
  const fetchUnitStatuses = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/rental/contracts/building/${building.id}?excludeClosed=true`, { method: "GET" }, session);
      if (!res.ok) throw new Error(`Contracten ophalen mislukt (${res.status} ${res.statusText})`);
      const contracts = await res.json();
      const map: Record<string, LettingStatus> = {};
      contracts.forEach((c: any) => {
        const unitId = c?.unit?.id;
        if (!unitId) return;
        const phase = c.phase;
        const status: LettingStatus =
          phase === "Contract in opzeg" ? "termination" : phase === "Gesloten" ? "vacant" : "rented";
        // "rented" wins over "termination" wins over "vacant" if multiple contracts.
        if (map[unitId] !== "rented") map[unitId] = status;
      });
      setUnitStatusById(map);
      setUnitTenantsById(buildUnitTenantMap(contracts));
    } catch (error) {
      // Non-fatal for the plan view, but report so occupancy colours/tenant list gaps are debuggable.
      console.error("Error fetching unit statuses:", error);
      Sentry.captureException(error, { extra: { buildingId: building.id, context: "plattegrondTab.fetchUnitStatuses" } });
      setUnitStatusById({});
      setUnitTenantsById({});
    }
  }, [building.id, session]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchPlans(), fetchAttributes(), fetchUnitStatuses()]).finally(() => setIsLoading(false));
  }, [fetchPlans, fetchAttributes, fetchUnitStatuses]);

  // Fetch images for every plan that has one (thumbnails in the grid + the editor view).
  useEffect(() => {
    plans.forEach((p) => {
      if (p.mimeType && !imageByPlan[p.id]) {
        fetchWithAuth(`${API_URL}/building-plans/${p.id}/image`, { method: "GET" }, session)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data) setImageByPlan((prev) => ({ ...prev, [p.id]: data }));
          })
          .catch(() => undefined);
      }
    });
  }, [plans, imageByPlan, session]);

  useEffect(() => {
    setPage(1);
    setNumPages(1);
    setContourTarget(null);
  }, [selectedPlanId]);

  // Deep link from the attribute plan icon: ?plan=&attribute= → open that plan + highlight.
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (deepLinkApplied.current || plans.length === 0) return;
    const planParam = searchParams.get("plan");
    const attributeParam = searchParams.get("attribute");
    if (planParam && plans.some((p) => p.id === Number(planParam))) {
      // Single specific plan → open it directly in the editor.
      setSelectedPlanId(Number(planParam));
      setMode("editor");
    } else if (attributeParam) {
      // No specific plan (the attribute sits on several) → show the grid filtered to that attribute.
      setMode("grid");
      setFocusAttributeId(Number(attributeParam));
    }
    if (attributeParam) {
      const key = `attribute:${attributeParam}`;
      setHighlightKey(key);
      window.setTimeout(() => setHighlightKey((k) => (k === key ? null : k)), 4000);
    }
    if (planParam || attributeParam) deepLinkApplied.current = true;
  }, [plans, searchParams]);

  // ── Plan CRUD ──────────────────────────────────────────────────────────────────────────
  const createPlan = () => setNameModal({ mode: "create", value: "" });
  const renamePlan = (plan: BuildingPlan) => setNameModal({ mode: "rename", planId: plan.id, value: plan.name });

  const submitNameModal = async () => {
    if (!nameModal) return;
    const name = nameModal.value.trim();
    if (!name) {
      toast.error("Geef een naam in");
      return;
    }
    setIsSavingName(true);
    try {
      if (nameModal.mode === "create") {
        const res = await fetchWithAuth(
          `${API_URL}/building-plans`,
          { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ buildingId: building.id, name }) },
          session,
        );
        if (!res.ok) throw new Error(res.statusText);
        const created: BuildingPlan = await res.json();
        await fetchPlans();
        setSelectedPlanId(created.id);
        setMode("editor");
        toast.success("Plattegrond aangemaakt — voeg nu de afbeelding toe");
      } else {
        const res = await fetchWithAuth(
          `${API_URL}/building-plans/${nameModal.planId}`,
          { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }) },
          session,
        );
        if (!res.ok) throw new Error(res.statusText);
        await fetchPlans();
      }
      setNameModal(null);
    } catch (error) {
      console.error("Error saving plan name:", error);
      toast.error(nameModal.mode === "create" ? "Fout bij aanmaken van plattegrond" : "Fout bij hernoemen");
    } finally {
      setIsSavingName(false);
    }
  };

  const deletePlan = async (planId: number) => {
    if (!window.confirm("Deze plattegrond verwijderen? De geplaatste pins en contouren gaan verloren.")) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/building-plans/${planId}`, { method: "DELETE" }, session);
      if (!res.ok) throw new Error(res.statusText);
      if (selectedPlanId === planId) {
        setSelectedPlanId(null);
        setMode("grid");
      }
      await fetchPlans();
      toast.success("Plattegrond verwijderd");
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("Fout bij verwijderen van plattegrond");
    }
  };

  // Upload (or replace) the image of a specific plan.
  const uploadPlanImage = useCallback(
    async (planId: number, file: File) => {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetchWithAuth(`${API_URL}/building-plans/${planId}/image`, { method: "POST", body: formData }, session);
        if (!res.ok) throw new Error(res.statusText);
        // Force the image cache to refresh for this plan.
        setImageByPlan((prev) => {
          const next = { ...prev };
          delete next[planId];
          return next;
        });
        await fetchPlans();
        toast.success("Afbeelding toegevoegd");
      } catch (error) {
        console.error("Error uploading plan image:", error);
        toast.error("Fout bij uploaden van afbeelding");
      } finally {
        setIsUploading(false);
      }
    },
    [session, fetchPlans],
  );

  // Paste (Ctrl/Cmd+V) a screenshot: into the open plan (editor), or as a new plan (grid).
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageItem = Array.from(items).find((i) => i.type.startsWith("image/"));
      if (!imageItem) return;
      const blob = imageItem.getAsFile();
      if (!blob) return;
      e.preventDefault();
      const ext = imageItem.type.split("/")[1] || "png";
      const file = new File([blob], `screenshot-${Date.now()}.${ext}`, { type: imageItem.type });

      if (mode === "editor" && selectedPlanId) {
        uploadPlanImage(selectedPlanId, file);
      } else {
        // Grid: create a plan then attach the pasted image.
        try {
          const res = await fetchWithAuth(
            `${API_URL}/building-plans`,
            { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ buildingId: building.id, name: "Geplakte plattegrond" }) },
            session,
          );
          if (!res.ok) throw new Error(res.statusText);
          const created: BuildingPlan = await res.json();
          await uploadPlanImage(created.id, file);
          await fetchPlans();
          setSelectedPlanId(created.id);
          setMode("editor");
        } catch (error) {
          console.error("Error creating plan from paste:", error);
          toast.error("Fout bij plakken van plattegrond");
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [mode, selectedPlanId, uploadPlanImage, building.id, session, fetchPlans]);

  // ── Placeable items + colours ────────────────────────────────────────────────────────
  const annotations = selectedPlan?.annotations || [];
  const placedKeys = useMemo(() => {
    const pins = new Set<string>();
    const contours = new Set<string>();
    annotations.forEach((a) => (a.shape === "pin" ? pins : contours).add(annKey(a)));
    return { pins, contours };
  }, [annotations]);

  const unitItems: PlanPlaceableItem[] = useMemo(
    () =>
      units
        .filter((u) => !!u.id)
        .map((u) => {
          const id = String(u.id);
          const key = `unit:${id}`;
          return {
            targetType: "unit" as const,
            id,
            name: id,
            subtitle: (u as any).referencePlus || undefined,
            color: statusColor(unitStatusById[id] || "vacant"),
            hasPin: placedKeys.pins.has(key),
            hasContour: placedKeys.contours.has(key),
          };
        }),
    [units, placedKeys, unitStatusById],
  );

  const attributeItems: PlanPlaceableItem[] = useMemo(
    () =>
      attributes.map((a) => {
        const key = `attribute:${a.id}`;
        return {
          targetType: "attribute" as const,
          id: String(a.id),
          name: a.name,
          subtitle: a.template?.category,
          color: getAttributeColor(a),
          hasPin: placedKeys.pins.has(key),
          hasContour: placedKeys.contours.has(key),
        };
      }),
    [attributes, placedKeys],
  );

  const labelFor = useCallback(
    (a: PlanAnnotation) => (a.targetType === "unit" ? a.unitId || "Unit" : a.buildingAttribute?.name || "Attribuut"),
    [],
  );

  const colorFor = useCallback(
    (a: PlanAnnotation) => {
      if (a.targetType === "unit") return statusColor(unitStatusById[a.unitId || ""] || "vacant");
      const attr = attributesById[a.buildingAttribute?.id ?? -1] || a.buildingAttribute;
      return getAttributeColor(attr);
    },
    [unitStatusById, attributesById],
  );

  // ── Tenant list ("Huurders op deze plattegrond") ────────────────────────────────────────
  // attributeId → tenant names, from the attributes' contractProducts (units come from contracts).
  const attributeTenantsById = useMemo(() => buildAttributeTenantMap(attributes), [attributes]);

  // tenant name → all unitIds that tenant rents in the building (regardless of placement).
  const unitsByTenant = useMemo(() => invertUnitTenants(unitTenantsById), [unitTenantsById]);

  // Tenant name(s) for a single placement.
  const tenantNamesForAnn = useCallback(
    (a: PlanAnnotation): string[] => {
      const targetId = a.targetType === "unit" ? a.unitId : a.buildingAttribute?.id;
      if (targetId == null || targetId === "") return [];
      return (a.targetType === "unit" ? unitTenantsById[String(targetId)] : attributeTenantsById[String(targetId)]) || [];
    },
    [unitTenantsById, attributeTenantsById],
  );

  // Tenant name(s) shown in the label on the plan itself (and on hover).
  const tenantFor = useCallback(
    (a: PlanAnnotation): string | null => {
      const names = tenantNamesForAnn(a);
      return names.length > 0 ? names.join(", ") : null;
    },
    [tenantNamesForAnn],
  );

  // Other units the placement's tenant(s) also rent in the building — so a garage contour shows
  // the apartment its tenant rents. Excludes the placement's own unit to avoid repeating the label.
  const extraUnitsFor = useCallback(
    (a: PlanAnnotation): string[] => {
      const names = tenantNamesForAnn(a);
      if (names.length === 0) return [];
      const ownUnit = a.targetType === "unit" ? String(a.unitId) : null;
      const units = new Set<string>();
      names.forEach((name) => (unitsByTenant[name] || []).forEach((u) => u !== ownUnit && units.add(u)));
      return Array.from(units).sort((x, y) => x.localeCompare(y, "nl", { numeric: true }));
    },
    [tenantNamesForAnn, unitsByTenant],
  );

  // Groups every placed unit/garage on the WHOLE plan (all pages) by its tenant.
  const tenantGroups = useMemo(
    () => groupAnnotationsByTenant(annotations, unitTenantsById, attributeTenantsById, labelFor, colorFor),
    [annotations, unitTenantsById, attributeTenantsById, labelFor, colorFor],
  );

  // Locate a placed item from the list: jump to its page (PDF) and briefly highlight it.
  const handleLocateListItem = useCallback(
    (item: PlanListItem) => {
      if (item.page && item.page !== page) setPage(item.page);
      setHighlightKey(item.key);
      window.setTimeout(() => setHighlightKey((k) => (k === item.key ? null : k)), 2500);
    },
    [page],
  );

  // ── Annotation persistence (auto-save) ──────────────────────────────────────────────────
  const upsertAnnotation = useCallback(
    async (body: any) => {
      if (!selectedPlanId) return;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/building-plans/${selectedPlanId}/annotation`,
          { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...body, page }) },
          session,
        );
        if (!res.ok) throw new Error(res.statusText);
        await fetchPlans();
      } catch (error) {
        console.error("Error saving annotation:", error);
        toast.error("Fout bij opslaan van positie");
      }
    },
    [selectedPlanId, page, session, fetchPlans],
  );

  const handleDropItem = (item: PlanDragItem, point: PlanPoint) =>
    upsertAnnotation({
      targetType: item.targetType,
      unitId: item.targetType === "unit" ? item.id : undefined,
      buildingAttributeId: item.targetType === "attribute" ? Number(item.id) : undefined,
      shape: "pin",
      x: point.x,
      y: point.y,
      color: item.color,
    });

  const handleMovePin = (a: PlanAnnotation, point: PlanPoint) =>
    upsertAnnotation({
      targetType: a.targetType,
      unitId: a.unitId ?? undefined,
      buildingAttributeId: a.buildingAttribute?.id,
      shape: "pin",
      x: point.x,
      y: point.y,
      color: a.color,
    });

  const handleSaveContour = (target: ContourTarget, points: PlanPoint[]) =>
    upsertAnnotation({
      targetType: target.targetType,
      unitId: target.targetType === "unit" ? target.id : undefined,
      buildingAttributeId: target.targetType === "attribute" ? Number(target.id) : undefined,
      shape: "contour",
      points,
      color: target.color,
    });

  const handleDeleteAnnotation = async (a: PlanAnnotation) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/building-plans/annotation/${a.id}`, { method: "DELETE" }, session);
      if (!res.ok) throw new Error(res.statusText);
      await fetchPlans();
    } catch (error) {
      console.error("Error deleting annotation:", error);
      toast.error("Fout bij verwijderen");
    }
  };

  const handleStartContour = (item: PlanPlaceableItem) => {
    const key = `${item.targetType}:${item.id}`;
    if (contourTarget && `${contourTarget.targetType}:${contourTarget.id}` === key) {
      setContourTarget(null);
    } else {
      setContourTarget({ targetType: item.targetType, id: item.id, name: item.name, color: item.color });
    }
  };

  const handleLocate = (item: PlanPlaceableItem) => {
    const key = `${item.targetType}:${item.id}`;
    setHighlightKey(key);
    window.setTimeout(() => setHighlightKey((k) => (k === key ? null : k)), 2500);
  };

  const startPanelResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = panelHeight;
    const onMove = (ev: PointerEvent) => setPanelHeight(Math.max(320, Math.min(1400, startHeight + (ev.clientY - startY))));
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const activeContourKey = contourTarget ? `${contourTarget.targetType}:${contourTarget.id}` : null;
  const image = selectedPlanId ? imageByPlan[selectedPlanId] : null;
  const isPdf = (image?.mimeType || selectedPlan?.mimeType || "").includes("pdf");

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-neutral-secondary-mid">Laden…</div>;
  }

  const Legend = () => (
    <div className="flex items-center gap-3 text-xs text-neutral-secondary-mid">
      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-status-good" /> Verhuurd</span>
      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-status-medium" /> In opzeg</span>
      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-status-bad" /> Leegstaand</span>
    </div>
  );

  const nameModalEl = nameModal ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={() => !isSavingName && setNameModal(null)}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-neutral-primary">
          {nameModal.mode === "create" ? "Nieuwe plattegrond" : "Plattegrond hernoemen"}
        </h3>
        <p className="mt-1 text-sm text-neutral-secondary-mid">
          {nameModal.mode === "create"
            ? "Geef de plattegrond een naam, bijv. “Garages” of “1ste verdieping”."
            : "Kies een nieuwe naam voor deze plattegrond."}
        </p>
        <input
          autoFocus
          value={nameModal.value}
          onChange={(e) => setNameModal((m) => (m ? { ...m, value: e.target.value } : m))}
          onKeyDown={(e) => {
            if (e.key === "Enter") submitNameModal();
            if (e.key === "Escape") setNameModal(null);
          }}
          placeholder="Naam van de plattegrond"
          className="mt-4 w-full rounded-lg border border-neutral-primary/20 px-3 py-2 text-sm text-neutral-primary outline-none focus:border-moss-primary"
        />
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setNameModal(null)}
            disabled={isSavingName}
            className="rounded-lg border border-neutral-primary/20 px-4 py-2 text-sm text-neutral-primary disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={submitNameModal}
            disabled={isSavingName || !nameModal.value.trim()}
            className="rounded-lg bg-brick-primary px-4 py-2 text-sm font-medium text-brick-secondary-fine disabled:opacity-50"
          >
            {isSavingName ? "Opslaan…" : nameModal.mode === "create" ? "Aanmaken" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ── GRID VIEW ──────────────────────────────────────────────────────────────────────────
  if (mode === "grid") {
    // A plan matches the free-text query when its name, or any pinned/contoured unit/attribute,
    // contains the query. The focusAttributeId (deep-link) further restricts to plans holding it.
    const q = gridQuery.trim().toLowerCase();
    const focusAttributeName = focusAttributeId != null ? attributesById[focusAttributeId]?.name : undefined;
    const filteredPlans = plans.filter((p) => {
      if (focusAttributeId != null) {
        const hasAttr = (p.annotations || []).some((a) => a.buildingAttribute?.id === focusAttributeId);
        if (!hasAttr) return false;
      }
      if (!q) return true;
      if (p.name.toLowerCase().includes(q)) return true;
      return (p.annotations || []).some((a) => {
        const label = a.targetType === "unit" ? a.unitId || "" : a.buildingAttribute?.name || "";
        return label.toLowerCase().includes(q);
      });
    });
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-neutral-primary">Plattegronden</h2>
            <p className="text-sm text-neutral-secondary-mid">
              Maak een plattegrond aan (bv. “Garages”, “1ste verdieping”), voeg de afbeelding toe en plaats er units
              en gebouwattributen op.
            </p>
          </div>
          <button type="button" onClick={createPlan} className="rounded-lg bg-brick-primary px-3 py-2 text-sm font-medium text-brick-secondary-fine">
            + Plattegrond toevoegen
          </button>
        </div>

        {/* Filter: only show plans that contain a matching unit/attribute pin or contour */}
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={gridQuery}
            onChange={(e) => setGridQuery(e.target.value)}
            placeholder="Filter op unit of gebouwattribuut…"
            className="w-full max-w-sm rounded-lg border border-neutral-primary/20 bg-white px-3 py-2 text-sm text-neutral-primary outline-none focus:border-moss-primary"
          />
          {focusAttributeName && (
            <span className="flex items-center gap-1 rounded-full bg-brick-primary/10 px-3 py-1 text-sm text-brick-primary">
              Gefilterd op: {focusAttributeName}
              <button type="button" onClick={() => setFocusAttributeId(null)} title="Filter wissen" className="font-semibold">×</button>
            </span>
          )}
          {(gridQuery || focusAttributeId != null) && (
            <span className="text-xs text-neutral-secondary-mid">{filteredPlans.length} van {plans.length} plattegronden</span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPlans.map((p) => {
            const img = imageByPlan[p.id];
            const count = (p.annotations || []).length;
            return (
              <div key={p.id} className="group overflow-hidden rounded-lg border border-neutral-primary/15 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlanId(p.id);
                    setMode("editor");
                  }}
                  className="block w-full"
                >
                  <div className="flex h-40 items-center justify-center overflow-hidden bg-neutral-100">
                    {img ? (
                      img.mimeType.includes("pdf") ? (
                        <span className="text-sm text-neutral-secondary-mid">PDF-plattegrond</span>
                      ) : (
                        <img src={img.dataUrl} alt={p.name} className="h-full w-full object-contain" />
                      )
                    ) : (
                      <span className="text-sm text-neutral-secondary-mid">Geen afbeelding — open om toe te voegen</span>
                    )}
                  </div>
                </button>
                <div className="flex items-center justify-between gap-2 p-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-neutral-primary">{p.name}</div>
                    <div className="text-xs text-neutral-secondary-mid">{count} geplaatst</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => renamePlan(p)} title="Hernoemen" className="rounded px-1 text-xs text-neutral-secondary-mid hover:text-brick-primary">
                      hernoem
                    </button>
                    <button type="button" onClick={() => deletePlan(p.id)} title="Verwijderen" className="rounded px-1 text-xs text-status-bad hover:underline">
                      ×
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={createPlan}
            className="flex h-[244px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-primary/30 bg-brick-secondary-fine text-neutral-secondary-mid hover:border-brick-primary hover:text-brick-primary"
          >
            <span className="text-3xl">+</span>
            <span className="text-sm font-medium">Plattegrond toevoegen</span>
            <span className="text-xs">of plak (Ctrl/Cmd+V) een schermafbeelding</span>
          </button>
        </div>
        {nameModalEl}
      </div>
    );
  }

  // ── EDITOR VIEW ────────────────────────────────────────────────────────────────────────
  const hasImage = !!image;
  return (
    <div className="space-y-3">
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && selectedPlanId) uploadPlanImage(selectedPlanId, file);
          e.target.value = "";
        }}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setMode("grid")} className="rounded-lg border border-neutral-primary/20 px-3 py-1.5 text-sm text-neutral-primary">
            ← Overzicht
          </button>
          <h2 className="text-xl font-semibold text-neutral-primary">{selectedPlan?.name}</h2>
          <button type="button" onClick={() => selectedPlan && renamePlan(selectedPlan)} className="text-xs text-neutral-secondary-mid hover:text-brick-primary">
            hernoem
          </button>
        </div>
        <button
          type="button"
          onClick={() => imageInputRef.current?.click()}
          disabled={isUploading}
          className="rounded-lg border border-neutral-primary/20 px-3 py-2 text-sm text-neutral-primary disabled:opacity-50"
        >
          {isUploading ? "Uploaden…" : hasImage ? "Afbeelding vervangen" : "Afbeelding toevoegen"}
        </button>
      </div>

      {!hasImage ? (
        <div
          onClick={() => imageInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            if (file && selectedPlanId) uploadPlanImage(selectedPlanId, file);
          }}
          className="flex h-72 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-primary/30 bg-brick-secondary-fine text-center text-neutral-secondary-mid hover:border-brick-primary"
        >
          <span className="font-medium text-neutral-primary">Voeg de afbeelding van “{selectedPlan?.name}” toe</span>
          <span className="text-sm">Sleep een PNG/JPG/PDF hierheen, klik om te kiezen, of plak (Ctrl/Cmd+V) een schermafbeelding</span>
        </div>
      ) : (
        <>
          <div className="flex gap-4" style={{ height: panelHeight }}>
            <PlanSidebar
              units={unitItems}
              attributes={attributeItems}
              activeContourKey={activeContourKey}
              onStartContour={handleStartContour}
              onLocate={handleLocate}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex items-center gap-1 rounded-lg border border-neutral-primary/20 bg-white px-1">
                  <button type="button" className="px-2 py-1 text-sm" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}>−</button>
                  <span className="w-12 text-center text-xs text-neutral-secondary-mid">{Math.round(zoom * 100)}%</span>
                  <button type="button" className="px-2 py-1 text-sm" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}>+</button>
                  <button type="button" className="px-2 py-1 text-xs text-neutral-secondary-mid" onClick={() => setZoom(1)}>reset</button>
                </div>
                {isPdf && numPages > 1 && (
                  <div className="flex items-center gap-1 rounded-lg border border-neutral-primary/20 bg-white px-2 py-1 text-sm">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="disabled:opacity-40">‹</button>
                    <span className="text-xs text-neutral-secondary-mid">p. {page}/{numPages}</span>
                    <button type="button" disabled={page >= numPages} onClick={() => setPage((p) => p + 1)} className="disabled:opacity-40">›</button>
                  </div>
                )}
                <div className="ml-auto"><Legend /></div>
              </div>
              <div className="min-h-0 flex-1">
                <PlanCanvas
                  imageDataUrl={image?.dataUrl || null}
                  mimeType={image?.mimeType || selectedPlan?.mimeType || ""}
                  isPdf={isPdf}
                  page={page}
                  onNumPages={setNumPages}
                  annotations={annotations.filter((a) => (a.page || 1) === page)}
                  labelFor={labelFor}
                  colorFor={colorFor}
                  tenantFor={tenantFor}
                  extraUnitsFor={extraUnitsFor}
                  zoom={zoom}
                  contourTarget={contourTarget}
                  onContourFinish={() => setContourTarget(null)}
                  onDropItem={handleDropItem}
                  onMovePin={handleMovePin}
                  onSaveContour={handleSaveContour}
                  onDeleteAnnotation={handleDeleteAnnotation}
                  highlightKey={highlightKey}
                />
              </div>
            </div>
          </div>
          <div onPointerDown={startPanelResize} title="Sleep om de werkruimte groter/kleiner te maken" className="flex h-3 cursor-row-resize items-center justify-center">
            <span className="h-1 w-16 rounded-full bg-neutral-primary/25" />
          </div>
          <PlanTenantList groups={tenantGroups} onLocate={handleLocateListItem} />
        </>
      )}
      {nameModalEl}
    </div>
  );
};

export default PlattegrondTab;
